import React, { useEffect, useMemo, useState } from 'react';
import './styles.scss';
import { PROFESSION_ICON, iconFor } from './icons';

interface ProfessionRank {
  id: number;
  name: string;
  xpRequired: number;
}

interface ProfessionConfig {
  id: string;
  name: string;
  category: 'job' | 'specialization';
  activities: string[];
  description?: string;
  type?: string;
  playstyle?: string;
  difficulty?: string;
  stats?: { label: string; value: number }[];
  tools?: string[];
  rankRewards?: string[];
}

interface OwnedProfession {
  professionId: string;
  xp: number;
  maxRankId: number;
  // Missing on saves from before this field existed - treated as full.
  endurance?: number;
  // Absent = active (older saves). Anything other than "active" is shown as
  // suspended: mirrors the server's isProfessionActive.
  status?: string;
}

const ENDURANCE_MAX = 100;

interface PlayerProfessions {
  jobs: OwnedProfession[];
  specialization: OwnedProfession | null;
  hasRerolled: boolean;
  // Suspended specializations displaced by a newly chosen one
  suspendedSpecializations?: OwnedProfession[];
}

const isActiveEntry = (e: OwnedProfession): boolean => e.status === undefined || e.status === null || e.status === 'active';

interface MenuDetail {
  ranks: ProfessionRank[];
  professions: ProfessionConfig[];
  ownProfessions: PlayerProfessions;
}

const PROFESSION_MENU_PREFIX = 'profession-menu:';
const PROFESSION_REROLL_MESSAGE = 'profession-reroll';
const MAX_JOBS = 2;

// Ignore any click during the first moments after the menu opens - browser
// focus flaps while it's toggled on, and a stray held click can land on an
// option and silently pick it (same guard as TargetMenu/CitySelection).
const CLICK_GRACE_MS = 250;

// Static decorative checklist - always the same for every profession, not
// read from any per-profession data. See PROFESSIONS-SYSTEM-HANDOFF.md's
// "Decorative UI fields" section: nothing here is actually enforced.
const STATIC_PREREQUISITES = ['Niveau minimum : 1', 'Aucune faction requise', 'Aucun prérequis de quête'];

const PIP_COUNT = 10;

type OwnedSlot = 'job1' | 'job2' | 'specialization';

interface OwnedInfo {
  slot: OwnedSlot;
  entry: OwnedProfession;
  label: string;
}

// Highest rank whose xpRequired is met, clamped to maxRankId if given -
// mirrors skymp5-server/ts/professions.ts's getRankForXp exactly (no shared
// module between the two projects).
function currentRank(ranks: ProfessionRank[], xp: number, maxRankId?: number): ProfessionRank | undefined {
  const uncapped = ranks.reduce<ProfessionRank | undefined>((best, r) => {
    if (r.xpRequired > xp) return best;
    if (!best || r.xpRequired > best.xpRequired) return r;
    return best;
  }, undefined);
  if (maxRankId === undefined || !uncapped || uncapped.id <= maxRankId) return uncapped;
  return ranks.find((r) => r.id === maxRankId) ?? uncapped;
}

// No "next rank" once the slot's cap has been reached - progress stops
// there permanently for that slot.
function nextRank(ranks: ProfessionRank[], rank: ProfessionRank | undefined, maxRankId?: number): ProfessionRank | undefined {
  if (rank && maxRankId !== undefined && rank.id >= maxRankId) return undefined;
  return ranks.find((r) => !rank || r.xpRequired > rank.xpRequired);
}

function findOwned(owned: PlayerProfessions, professionId: string): OwnedInfo | null {
  if (owned.jobs[0]?.professionId === professionId) return { slot: 'job1', entry: owned.jobs[0], label: 'Métier principal' };
  if (owned.jobs[1]?.professionId === professionId) return { slot: 'job2', entry: owned.jobs[1], label: 'Second métier' };
  // A job past the second only exists as a replacement made while another was suspended
  const extraJob = owned.jobs.slice(2).find((j) => j.professionId === professionId);
  if (extraJob) return { slot: 'job2', entry: extraJob, label: 'Métier' };
  if (owned.specialization?.professionId === professionId) return { slot: 'specialization', entry: owned.specialization, label: 'Spécialisation' };
  const parked = owned.suspendedSpecializations?.find((e) => e.professionId === professionId);
  if (parked) return { slot: 'specialization', entry: parked, label: 'Spécialisation' };
  return null;
}

// Only ACTIVE slots count toward the limits - a suspended one never blocks a new pick.
function activeJobCount(owned: PlayerProfessions): number {
  return owned.jobs.filter(isActiveEntry).length;
}

function hasActiveSpecialization(owned: PlayerProfessions): boolean {
  return !!owned.specialization && isActiveEntry(owned.specialization);
}

function hasSuspended(owned: PlayerProfessions): boolean {
  return owned.jobs.some((j) => !isActiveEntry(j))
    || (!!owned.specialization && !isActiveEntry(owned.specialization))
    || (owned.suspendedSpecializations?.length ?? 0) > 0;
}

const ProfessionsMenu: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState<MenuDetail | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmingReroll, setConfirmingReroll] = useState(false);
  const openedAtRef = React.useRef(0);

  useEffect(() => {
    const showHandler = (e: Event) => {
      const d = (e as CustomEvent).detail as MenuDetail;
      setDetail(d);
      const firstOwnedId = d.ownProfessions.jobs[0]?.professionId ?? d.ownProfessions.specialization?.professionId;
      setSelectedId(firstOwnedId ?? d.professions[0]?.id ?? null);
      setConfirming(false);
      setConfirmingReroll(false);
      setVisible(true);
      openedAtRef.current = Date.now();
    };
    const hideHandler = () => setVisible(false);

    window.addEventListener('skymp5-client:showProfessionsMenu', showHandler);
    window.addEventListener('skymp5-client:hideProfessionsMenu', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showProfessionsMenu', showHandler);
      window.removeEventListener('skymp5-client:hideProfessionsMenu', hideHandler);
    };
  }, []);

  const selected = useMemo(
    () => detail?.professions.find((p) => p.id === selectedId) ?? null,
    [detail, selectedId]
  );

  const ownedInfo = useMemo(
    () => (detail && selected ? findOwned(detail.ownProfessions, selected.id) : null),
    [detail, selected]
  );

  // Whether `selected` could still be chosen right now (its category's slot
  // budget isn't already spent by something else).
  const choosable = useMemo(() => {
    if (!detail || !selected || ownedInfo) return false;
    if (selected.category === 'job') return activeJobCount(detail.ownProfessions) < MAX_JOBS;
    return !hasActiveSpecialization(detail.ownProfessions);
  }, [detail, selected, ownedInfo]);

  // The explanatory note shown before confirming a job/specialization pick -
  // this is the whole point of asking up front: the player must understand
  // a 2nd job can never reach Maître before they lock it in.
  const pickConsequence = useMemo(() => {
    if (!detail || !selected) return '';
    if (selected.category === 'specialization') return 'Spécialisation - pourra atteindre le rang Maître.';
    // Same rule as the server: the top rank is available unless a job slot
    // (even a suspended one) already holds it.
    const topRankId = detail.ranks.reduce((max, r) => Math.max(max, r.id), 0);
    return !detail.ownProfessions.jobs.some((j) => j.maxRankId >= topRankId)
      ? 'Ce sera votre métier PRINCIPAL - pourra atteindre le rang Maître.'
      : 'Ce sera votre SECOND métier - plafonné au rang Expert (ne pourra jamais atteindre Maître).';
  }, [detail, selected]);

  const openConfirm = (e: React.MouseEvent) => {
    // e.detail === 0 means the "click" came from the keyboard (Enter/Space on
    // the DOM-focused button), not a real pointer click.
    if (e.detail === 0 || !selected || !choosable) return;
    if (Date.now() - openedAtRef.current < CLICK_GRACE_MS) return;

    setConfirming(true);
  };

  const sendChoice = () => {
    if (!selected) return;
    setVisible(false);
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage(PROFESSION_MENU_PREFIX + selected.id);
    }
  };

  const hasAnyProfession = !!detail && (detail.ownProfessions.jobs.length > 0 || !!detail.ownProfessions.specialization);
  // The free reroll is refused by the server while a slot is suspended (staff decision)
  const anySuspended = !!detail && hasSuspended(detail.ownProfessions);
  const canReroll = hasAnyProfession && !detail?.ownProfessions.hasRerolled && !anySuspended;

  const openRerollConfirm = (e: React.MouseEvent) => {
    if (e.detail === 0 || !canReroll) return;
    if (Date.now() - openedAtRef.current < CLICK_GRACE_MS) return;
    setConfirmingReroll(true);
  };

  const sendReroll = () => {
    setVisible(false);
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage(PROFESSION_REROLL_MESSAGE);
    }
  };

  if (!visible || !detail || !selected) return null;

  const { ranks, professions, ownProfessions } = detail;
  const jobProfessions = professions.filter((p) => p.category === 'job');
  const specProfessions = professions.filter((p) => p.category === 'specialization');

  const renderCategoryItem = (p: ProfessionConfig) => {
    const info = findOwned(ownProfessions, p.id);
    const isSelected = p.id === selectedId;
    const isLocked = !info
      && (p.category === 'job' ? activeJobCount(ownProfessions) >= MAX_JOBS : hasActiveSpecialization(ownProfessions));
    const isSuspended = !!info && !isActiveEntry(info.entry);
    return (
      <li key={p.id}>
        <button
          type="button"
          className={[
            'profession-menu-category',
            isSelected ? 'profession-menu-category--selected' : '',
            info ? 'profession-menu-category--current' : '',
            isLocked ? 'profession-menu-category--locked' : '',
            isSuspended ? 'profession-menu-category--suspended' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => setSelectedId(p.id)}
        >
          {info && <span className="profession-menu-category__badge" title={info.label} />}
          <span className="profession-menu-category__icon">{PROFESSION_ICON[p.id]}</span>
          <span className="profession-menu-category__text">
            <span className="profession-menu-category__name">{p.name}</span>
            <span className="profession-menu-category__desc">
              {info ? info.label : p.type}
              {isSuspended && <span className="profession-menu-suspended-tag">Suspendu</span>}
            </span>
          </span>
        </button>
      </li>
    );
  };
  const rank = ownedInfo ? currentRank(ranks, ownedInfo.entry.xp, ownedInfo.entry.maxRankId) : undefined;
  const next = ownedInfo ? nextRank(ranks, rank, ownedInfo.entry.maxRankId) : undefined;
  // The diamond row skips the starting rank (Novice) - it has nothing to
  // reward, matching the reference's 4 milestone badges.
  const milestoneRanks = ranks.slice(1);

  const selectedSuspended = !!ownedInfo && !isActiveEntry(ownedInfo.entry);

  const buttonLabel = selectedSuspended
    ? 'Métier suspendu'
    : ownedInfo
      ? `${ownedInfo.label} actuel${ownedInfo.slot === 'specialization' ? 'le' : ''}`
      : choosable
        ? 'Choisir cette voie'
        : selected.category === 'job'
          ? '2 métiers déjà choisis'
          : 'Spécialisation déjà choisie';

  return (
    <div className="profession-menu-overlay">
      <div className="profession-menu-shell">
        <header className="profession-menu-header">
          <div className="profession-menu-header__brand">Altherys RP</div>
          <h1 className="profession-menu-header__title">Sélecteur de métiers</h1>
          <div className="profession-menu-header__tagline">Choisissez votre voie, forgez votre destinée.</div>
        </header>

        <div className="profession-menu-body">
          <aside className="profession-menu-categories">
            <div className="profession-menu-heading">Catégories</div>
            <div className="profession-menu-category-groups">
              <div className="profession-menu-category-group">
                <div className="profession-menu-category-group__label profession-menu-category-group__label--jobs">Métiers</div>
                <ul className="profession-menu-category-list">
                  {jobProfessions.map(renderCategoryItem)}
                </ul>
              </div>
              <div className="profession-menu-category-group">
                <div className="profession-menu-category-group__label profession-menu-category-group__label--spec">Spécialisation</div>
                <ul className="profession-menu-category-list">
                  {specProfessions.map(renderCategoryItem)}
                </ul>
              </div>
            </div>
            <div className="profession-menu-categories__hint">Choisissez une voie pour en savoir plus.</div>
          </aside>

          <div className="profession-menu-divider" />

          <main className={`profession-menu-detail${selectedSuspended ? ' profession-menu-detail--suspended' : ''}`}>
            <h2 className="profession-menu-detail__title">
              {selected.name}
              {selectedSuspended && <span className="profession-menu-suspended-tag">Suspendu</span>}
            </h2>

            <div className="profession-menu-detail__hero">
              <span className="profession-menu-detail__hero-icon">{PROFESSION_ICON[selected.id]}</span>
            </div>

            <p className="profession-menu-detail__description">{selected.description ?? ' '}</p>

            <div className="profession-menu-section">
              <div className="profession-menu-heading">Aptitudes liées</div>
              <div className="profession-menu-stats">
                {(selected.stats ?? []).map((s) => (
                  <div className="profession-menu-stat" key={s.label}>
                    <span className="profession-menu-stat__label">{s.label}</span>
                    <span className="profession-menu-stat__pips">
                      {Array.from({ length: PIP_COUNT }).map((_, i) => (
                        <span
                          key={i}
                          className={`profession-menu-stat__pip${i < Math.round((s.value / 100) * PIP_COUNT) ? ' profession-menu-stat__pip--filled' : ''}`}
                        />
                      ))}
                    </span>
                    <span className="profession-menu-stat__value">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="profession-menu-section">
              <div className="profession-menu-heading">
                {ownedInfo ? 'Progression' : 'Récompenses de niveau'}
              </div>
              <div className="profession-menu-ladder">
                {milestoneRanks.map((r, i) => {
                  const beyondCap = !!ownedInfo && r.id > ownedInfo.entry.maxRankId;
                  const reached = !!ownedInfo && !beyondCap && ownedInfo.entry.xp >= r.xpRequired;
                  const isCurrentRank = !!ownedInfo && rank?.id === r.id;
                  return (
                    <div
                      key={r.id}
                      className={[
                        'profession-menu-diamond',
                        reached ? 'profession-menu-diamond--reached' : '',
                        isCurrentRank ? 'profession-menu-diamond--current' : '',
                        beyondCap ? 'profession-menu-diamond--capped' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div className="profession-menu-diamond__shape" />
                      <span className="profession-menu-diamond__rank">{r.name}</span>
                      <span className="profession-menu-diamond__xp">{beyondCap ? 'Plafond' : `${r.xpRequired} XP`}</span>
                      <div className="profession-menu-diamond__reward">
                        {selected.rankRewards?.[i] && !beyondCap ? selected.rankRewards[i] : ''}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={`profession-menu-progress-bar${ownedInfo ? '' : ' profession-menu-progress-bar--hidden'}`}>
                {ownedInfo && (
                  <div
                    className="profession-menu-progress-bar__fill"
                    style={{ width: `${next && next.xpRequired > 0 ? Math.min(1, ownedInfo.entry.xp / next.xpRequired) * 100 : 100}%` }}
                  />
                )}
              </div>
              <div className={`profession-menu-progress-text${ownedInfo ? '' : ' profession-menu-progress-text--hidden'}`}>
                {ownedInfo
                  ? (next
                    ? `${ownedInfo.entry.xp} / ${next.xpRequired} XP vers ${next.name}`
                    : `${ownedInfo.entry.xp} XP - plafond de ce métier atteint`)
                  : ' '}
              </div>

            </div>
          </main>

          <div className="profession-menu-divider" />

          <aside className="profession-menu-info">
            <div className="profession-menu-heading">Informations</div>
            <dl className="profession-menu-infolist">
              <div><dt>Niveau requis</dt><dd>1</dd></div>
              <div><dt>Type</dt><dd>{selected.type ?? '-'}</dd></div>
              <div><dt>Style de jeu</dt><dd>{selected.playstyle ?? '-'}</dd></div>
              <div>
                <dt>Difficulté</dt>
                <dd>
                  {selected.difficulty && (
                    <span className={`profession-menu-difficulty-dot profession-menu-difficulty-dot--${selected.difficulty.toLowerCase()}`} />
                  )}
                  {selected.difficulty ?? '-'}
                </dd>
              </div>
            </dl>

            <div className="profession-menu-heading">Prérequis</div>
            <ul className="profession-menu-checklist">
              {STATIC_PREREQUISITES.map((req) => (
                <li key={req}>
                  <span>{req}</span>
                  <span className="profession-menu-checklist__check" aria-hidden="true" />
                </li>
              ))}
            </ul>

            <div className="profession-menu-heading">Outils nécessaires</div>
            <div className="profession-menu-tools">
              {(selected.tools ?? []).map((tool) => (
                <div className="profession-menu-tool" key={tool}>
                  <span className="profession-menu-tool__icon">{iconFor(tool)}</span>
                  <span className="profession-menu-tool__name">{tool}</span>
                </div>
              ))}
            </div>

            {ownedInfo && (
              <div className="profession-menu-endurance-section">
                <div className="profession-menu-heading">Endurance</div>
                <div className="profession-menu-endurance">
                  <div className="profession-menu-endurance-bar">
                    <div
                      className="profession-menu-endurance-bar__fill"
                      style={{ width: `${Math.max(0, Math.min(ENDURANCE_MAX, ownedInfo.entry.endurance ?? ENDURANCE_MAX))}%` }}
                    />
                  </div>
                  <span className="profession-menu-endurance__value">
                    {Math.round(ownedInfo.entry.endurance ?? ENDURANCE_MAX)} / {ENDURANCE_MAX}
                  </span>
                </div>
              </div>
            )}
          </aside>
        </div>

        <footer className="profession-menu-footer">
          <div className="profession-menu-footer__hint">
            <kbd>Échap</kbd> Fermer
          </div>
          <button
            type="button"
            className={`profession-menu-choose-btn${ownedInfo ? ' profession-menu-choose-btn--current' : ''}`}
            disabled={!choosable}
            onClick={openConfirm}
          >
            {buttonLabel}
          </button>
          <div className="profession-menu-footer__spacer">
            {hasAnyProfession && (
              <button
                type="button"
                className="profession-menu-reroll-btn"
                disabled={!canReroll}
                onClick={openRerollConfirm}
                title={detail?.ownProfessions.hasRerolled
                  ? 'Réinitialisation déjà utilisée - demande à un admin.'
                  : anySuspended ? 'Un de tes métiers est suspendu - demande à un admin.' : undefined}
              >
                {detail?.ownProfessions.hasRerolled ? 'Réinitialisation utilisée' : 'Réinitialiser mes métiers'}
              </button>
            )}
          </div>
        </footer>

        {confirming && (
          <div className="profession-menu-confirm-overlay">
            <div className="profession-menu-confirm">
              <div className="profession-menu-confirm__title">Confirmer votre choix</div>
              <p className="profession-menu-confirm__text">
                Vous êtes sur le point de choisir le métier :
                <span className="profession-menu-confirm__profession">{selected.name}</span>
              </p>
              <div className="profession-menu-confirm__note">{pickConsequence}</div>
              <div className="profession-menu-confirm__actions">
                <button
                  type="button"
                  className="profession-menu-confirm-btn profession-menu-confirm-btn--cancel"
                  onClick={() => setConfirming(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="profession-menu-confirm-btn profession-menu-confirm-btn--confirm"
                  onClick={sendChoice}
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmingReroll && (
          <div className="profession-menu-confirm-overlay">
            <div className="profession-menu-confirm">
              <div className="profession-menu-confirm__title">Réinitialiser tes métiers ?</div>
              <p className="profession-menu-confirm__text">
                Tes 2 métiers et ta spécialisation seront effacés et devront être choisis à nouveau.
              </p>
              <div className="profession-menu-confirm__note">
                Cette réinitialisation est gratuite une seule fois. Toute réinitialisation suivante devra passer par un administrateur.
              </div>
              <div className="profession-menu-confirm__actions">
                <button
                  type="button"
                  className="profession-menu-confirm-btn profession-menu-confirm-btn--cancel"
                  onClick={() => setConfirmingReroll(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="profession-menu-confirm-btn profession-menu-confirm-btn--danger"
                  onClick={sendReroll}
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionsMenu;
